import {
  ButtonDS,
  InputDS,
  Spacer,
  TextCapitalized,
  ArrowForwardOutlined,
  hasEmailFormat,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import styled from "styled-components";
import {
  addCompanyToCompaniesAccessAsync,
  selectManagersInCompanyGroup,
} from "./../usersSlice";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { UserInformations } from "./addUserPage";
import "../../../../i18n/i18n";
import { SelectExistingUser } from "./selectExistingUser";
import { selectCompany } from "../../homeSlice";
import { useNavigate } from "react-router-dom";

type AddUserInformationsProps = {
  firstName: string;
  lastName: string;
  email: string;
  job: string;
  existingManagerUuid: string | null;
  onSubmit: (informations: UserInformations) => void;
  setFirstName: (firstName: string) => void;
  setLastName: (lastName: string) => void;
  setEmail: (email: string) => void;
  setJob: (job: string) => void;
  onSelectExistingManager: (uuid: string | null) => void;
  resetValue: () => void;
}

export const AddUserInformations = (props: AddUserInformationsProps) => {
  const { firstName, lastName, email, job, existingManagerUuid, onSubmit, setFirstName, setLastName, setEmail, setJob, onSelectExistingManager, resetValue } = props;
  const { t } = useTranslation();
  const [displayError, setDisplayError] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const managersInCompanyGroup = useAppSelector(selectManagersInCompanyGroup);
  const company = useAppSelector(selectCompany);

  const duplicateUser = useMemo(() => {
    if (!email) return null;
    return managersInCompanyGroup.find(
      (manager) => manager.email?.toUpperCase() === email.toUpperCase(),
    ) ?? null;
  }, [email, managersInCompanyGroup]);

  const hasDuplicateEmail: boolean = useMemo(() => !!duplicateUser, [duplicateUser]);
  const isDepartementUser: boolean = useMemo(() => duplicateUser?.role === "DEPARTMENT_USER", [duplicateUser]);

  const isValidForm: boolean = useMemo(
    () => {
      if (existingManagerUuid) return true;

      return !!(
        firstName &&
        lastName &&
        email &&
        hasEmailFormat(email) &&
        !hasDuplicateEmail
      );
    },
    [email, firstName, hasDuplicateEmail, lastName, existingManagerUuid],
  );

  const clickToSubmit = useCallback(() => {
    if (!isValidForm && !existingManagerUuid) {
      setDisplayError(true);
      return;
    }

    if (existingManagerUuid && company) {
      void dispatch(addCompanyToCompaniesAccessAsync({ managerUuid: existingManagerUuid, companyUuid: company.uuid }));
      navigate("/home/users/management");
    } else {
      onSubmit({ firstName, lastName, email, job });
    }
  }, [isValidForm, existingManagerUuid, company, dispatch, navigate, onSubmit, firstName, lastName, email, job]);

  const handleSelectExistingManager = useCallback((uuid: string | null) => {
    if (!uuid) return onSelectExistingManager(null);

    setDisplayError(false);
    resetValue();
    onSelectExistingManager(uuid);
  }, [onSelectExistingManager, resetValue]);

  return (
    <StyledModalContainer>
      <StyledModalContent>
        <StyledTitle>
          <TextCapitalized>
            {t(`users.modal.informations.title`)}
          </TextCapitalized>
        </StyledTitle>
        
        <Spacer y={3} />
        <SelectExistingUser selectedExistingManagerUuid={handleSelectExistingManager} />

        <Spacer y={2} />
        <StyledLeftAlign>
          <TextCapitalized>{t(`users.modal.informations.create`)}</TextCapitalized>
        </StyledLeftAlign>
        <Spacer y={1} />
        <StyledFlex>
          <InputDS
            label={t("users.modal.firstName") || ""}
            placeholder={t("users.modal.firstName.placeholder")}
            error={
              displayError && !firstName
                ? t("users.modal.error.input")
                : undefined
            }
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
            isDisabled={!!existingManagerUuid}
          />
          <InputDS
            label={t("users.modal.lastName") || ""}
            placeholder={t("users.modal.lastName.placeholder")}
            error={
              displayError && !lastName
                ? t("users.modal.error.input")
                : undefined
            }
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
            isDisabled={!!existingManagerUuid}
          />
        </StyledFlex>
        <InputDS
          label={t("users.modal.email") || ""}
          placeholder={t("users.modal.email.placeholder")}
          error={
            displayError && !email
              ? t("users.modal.error.input")
              : displayError && !hasEmailFormat(email)
                ? t("users.modal.error.emailFormat")
                : hasDuplicateEmail
                  ? isDepartementUser
                    ? t("users.modal.error.departementUser.alreadyExists")
                    : t("users.modal.error.duplicateEmail")
                  : undefined
          }
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
          isPlaceholderUppercase
          isDisabled={!!existingManagerUuid}
        />
        <InputDS
          label={t("users.modal.job") || ""}
          placeholder={t("users.modal.job.placeholder")}
          value={job}
          onChange={(e) => setJob(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
          isDisabled={!!existingManagerUuid}
        />
        <Spacer y={0.75} />
        <div>
          <ButtonDS
            rightIcon={<ArrowForwardOutlined />}
            text={existingManagerUuid ? t("users.modal.informations.addExistingManager") : t("users.modal.informations.submit")}
            format={"hug"}
            buttonType={"primary"}
            onClick={clickToSubmit}
            disabled={!isValidForm}
          />
        </div>
      </StyledModalContent>
    </StyledModalContainer>
  );
};

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledModalContent = styled.div`
  flex-direction: column;
  display: flex;
  width: 33rem;
  align-items: center;
`;

const StyledFlex = styled.div`
  display: flex;
  width: 100%;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H1"]}
`;

const StyledLeftAlign = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`;